import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp, Trash } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { PhoneInput } from "./ui/phone";
import { T } from "@/i18n";

export type Contact = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  type?: string | null;
};

export function ContactsInput({
  id,
  contacts,
  onChange,
  className,
  addTranslation,
  nameTranslation,
  phoneTranslation,
  emailTranslation,
  typeTranslation,
  hideType,
  extras,
}: {
  id?: string;
  contacts: Contact[];
  onChange: (contacts: Contact[]) => void;
  className?: string;
  addTranslation?: string;
  nameTranslation?: string;
  phoneTranslation?: string;
  emailTranslation?: string;
  typeTranslation?: string;
  hideType?: boolean;
  extras?: (index: number) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const [visibilityList, setVisible] = useState<boolean[]>([]);
  const handlePropertyChange = (
    i: number,
    property: keyof Contact,
    value: string | undefined
  ) => {
    const newContact = { ...contacts[i] };
    newContact[property] = value;
    onChange([...contacts.slice(0, i), newContact, ...contacts.slice(i + 1)]);
  };
  return (
    <div className={cn("flex flex-col w-full", className)}>
      {contacts.map((contact, i) => (
        <div key={i} className="flex gap-4 items-start py-4 border-b">
          <Button
            variant="ghost"
            className="text-primary"
            type="button"
            onClick={() => {
              setVisible((v) => {
                const newV = [...v];
                newV[i] = !v[i];
                return newV;
              });
            }}
          >
            {visibilityList[i] ? <ChevronUp /> : <ChevronDown />}
          </Button>

          <div className="grid gap-2 w-full">
            <Input
              placeholder={t(nameTranslation ?? "common.inputs.contact.name")}
              value={contact.name ?? undefined}
              onChange={(e) => {
                handlePropertyChange(i, "name", e.target.value);
              }}
            />
            {visibilityList[i] && (
              <>
                {!hideType && (
                  <Input
                    placeholder={t(
                      typeTranslation ?? "common.inputs.contact.type"
                    )}
                    value={contact.type ?? undefined}
                    onChange={(e) => {
                      handlePropertyChange(i, "type", e.target.value);
                    }}
                  />
                )}
                <Input
                  type="email"
                  inputMode="email"
                  placeholder={t(
                    emailTranslation ?? "common.inputs.contact.email"
                  )}
                  value={contact.email ?? undefined}
                  onChange={(e) => {
                    handlePropertyChange(i, "email", e.target.value);
                  }}
                />

                <PhoneInput
                  value={contact.phone ?? ""}
                  placeholder={t(
                    phoneTranslation ?? "common.inputs.contact.phone"
                  )}
                  onChange={(v) => {
                    handlePropertyChange(i, "phone", v);
                  }}
                />
                {extras?.(i)}
              </>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            className="text-destructive"
            onClick={() => {
              onChange(contacts.filter((_, j) => j !== i));
            }}
          >
            <Trash />
          </Button>
        </div>
      ))}
      <Button
        id={id}
        className="mt-4"
        type="button"
        variant="outline"
        onClick={() => {
          onChange([...contacts, {}]);
        }}
      >
        <T tKey={addTranslation ?? "common.inputs.contact.add"} />
      </Button>
    </div>
  );
}
